import React, { useEffect, useState } from 'react';
import { matchPaymentsAPI, connectAPI } from '@/lib/api';
import { openCheckout } from '@/lib/stripe';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { CreditCard, Lock, AlertTriangle } from 'lucide-react';

const euro = (n) => `€${Number(n || 0).toFixed(2)}`;

/**
 * The field collection of a match: the organizer opens it, players block their
 * share (charged one hour before kick-off) or pay it in the last hour.
 */
const parseEuro = (v) => parseFloat(String(v).replace(',', '.'));
// Rounded up to the cent, so the shares cover the field.
const splitShare = (cost, players) => (cost > 0 ? (Math.ceil((cost / (players || 10)) * 100) / 100).toFixed(2) : '');

export const FieldCollection = ({ matchId, data, maxPlayers, currentUserId, myCov, isTitular, onPayOthers, onChanged }) => {
  const col = data.collection;
  const [target, setTarget] = useState(data.suggested_target ? String(data.suggested_target) : '');
  const [share, setShare] = useState(splitShare(data.suggested_target, maxPlayers));
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [collector, setCollector] = useState(null);
  const canOpen = !col && data.is_admin && data.collections_enabled;

  useEffect(() => {
    if (!canOpen) return;
    matchPaymentsAPI.collectorCandidates(matchId).then((res) => {
      setCandidates(res.data);
      setCollector(res.data.find((c) => c.ready) || null);
    }).catch(() => setCandidates([]));
  }, [canOpen, matchId]);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Qualcosa non è andato');
    } finally {
      setBusy(false);
    }
  };

  const changeTarget = (value) => {
    setTarget(value);
    setShare(splitShare(parseEuro(value), maxPlayers));
  };

  const openCollection = () => run(async () => {
    const amount = parseEuro(target);
    if (!(amount > 0) || !(parseEuro(share) > 0)) {
      toast.error('Indica il costo del campo e la quota a testa');
      return;
    }
    if (!collector) {
      toast.error('Scegli chi raccoglie i soldi');
      return;
    }
    const res = await matchPaymentsAPI.openCollection(matchId, {
      target: amount, share: parseEuro(share), collector_type: collector.type, collector_id: collector.id,
    });
    if (res.data.needs_stripe) {
      toast.error('Collega prima il tuo conto Stripe');
      return;
    }
    toast.success('Raccolta aperta');
    onChanged();
  });

  const ask = (c) => run(async () => {
    await matchPaymentsAPI.askCollector(matchId, c.id);
    toast.success(`Ho chiesto a ${c.name} di collegare Stripe`);
  });

  const linkStripe = () => run(async () => {
    const res = await connectAPI.link({ origin: window.location.origin, return_path: `/matches/${matchId}` });
    await openCheckout(res.data.url);
  });

  const pay = () => run(async () => {
    const res = await matchPaymentsAPI.checkout(matchId, { origin: window.location.origin });
    await openCheckout(res.data.checkout_url);
  });

  const cancel = () => {
    if (!window.confirm('Annullare la raccolta? I blocchi sulle carte spariscono e chi ha già pagato viene rimborsato.')) return;
    run(async () => {
      await matchPaymentsAPI.closeCollection(matchId);
      toast.success('Raccolta annullata');
      onChanged();
    });
  };

  // ── No collection yet: the organizer opens it and picks who collects ──
  if (!col) {
    if (!canOpen) return null;
    return (
      <div className="rounded-lg border border-dashed p-3 space-y-3" data-testid="open-collection">
        <div>
          <p className="text-sm font-medium">Raccogli i soldi del campo con carta</p>
          <p className="text-xs text-muted-foreground">
            I giocatori bloccano la quota sulla carta; un'ora prima della partita viene addebitata e arriva
            sul conto di chi raccoglie. Chi si ritira prima non paga nulla.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">Costo campo €</span>
            <Input inputMode="decimal" value={target} onChange={(e) => changeTarget(e.target.value)} className="h-9" data-testid="collection-target" />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-muted-foreground">Quota a testa €</span>
            <Input inputMode="decimal" value={share} onChange={(e) => setShare(e.target.value)} className="h-9" data-testid="collection-share" />
          </label>
        </div>
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">Chi raccoglie i soldi (serve un conto Stripe verificato)</p>
          {!candidates ? (
            <p className="text-xs text-muted-foreground">Caricamento…</p>
          ) : candidates.map((c) => {
            const picked = collector?.type === c.type && collector?.id === c.id;
            return (
              <div
                key={`${c.type}:${c.id}`}
                className={`flex items-center justify-between gap-2 p-2 rounded-lg border text-sm ${picked ? 'border-primary bg-primary/5' : ''}`}
              >
                <label className={`flex items-center gap-2 min-w-0 ${c.ready ? 'cursor-pointer' : 'opacity-60'}`}>
                  <input type="radio" name="collector" disabled={!c.ready} checked={picked} onChange={() => setCollector(c)} />
                  <span className="truncate">
                    {c.name}
                    {c.type === 'facility' && <span className="text-muted-foreground"> · struttura</span>}
                    {c.id === currentUserId && <span className="text-muted-foreground"> · tu</span>}
                  </span>
                </label>
                {c.ready ? (
                  <span className="text-xs text-emerald-600 shrink-0">conto verificato</span>
                ) : c.id === currentUserId ? (
                  <button type="button" onClick={linkStripe} disabled={busy} className="text-xs text-primary hover:underline shrink-0">Collega il tuo Stripe</button>
                ) : c.type === 'user' ? (
                  <button type="button" onClick={() => ask(c)} disabled={busy} className="text-xs text-primary hover:underline shrink-0">Chiedi di collegarlo</button>
                ) : (
                  <span className="text-xs text-muted-foreground shrink-0">senza Stripe</span>
                )}
              </div>
            );
          })}
        </div>
        <Button size="sm" className="w-full h-9" onClick={openCollection} disabled={busy || !collector}>Apri raccolta</Button>
      </div>
    );
  }

  const pct = Math.min(100, Math.round((col.collected / col.target) * 100));
  const blocking = col.phase === 'early' || col.phase === 'blocking';
  const covered = !!myCov;
  const late = col.phase === 'late';
  const toPay = col.share + (late ? col.penalty.euro : 0);

  return (
    <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 space-y-2" data-testid="field-collection">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium">Raccolta per il campo</span>
        <span className="font-mono shrink-0">{euro(col.collected)} / {euro(col.target)}</span>
      </div>
      <div className="h-2 bg-emerald-100 dark:bg-emerald-950 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-emerald-500 to-blue-600 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-muted-foreground">
        {blocking
          ? `${col.held_count} ${col.held_count === 1 ? 'giocatore ha' : 'giocatori hanno'} bloccato la quota · addebito un'ora prima della partita`
          : 'Quote addebitate'}
        {' · '}i soldi vanno a {col.collector_name}
      </p>

      {isTitular && !covered && col.phase === 'early' && (
        <p className="text-xs">La quota si blocca dai 5 giorni prima della partita.</p>
      )}

      {isTitular && !covered && col.phase === 'blocking' && (
        <>
          <Button onClick={pay} disabled={busy} className="w-full h-11 bg-gradient-to-br from-emerald-500 to-blue-600 hover:opacity-90" data-testid="block-quota-btn">
            <Lock className="h-4 w-4 mr-2" />
            Blocca {euro(col.share)} sulla carta
          </Button>
          <p className="text-xs text-muted-foreground">
            Adesso non ti addebitiamo nulla: i {euro(col.share)} ti vengono addebitati un'ora prima della partita.
            Se ti ritiri prima, il blocco sparisce.
          </p>
        </>
      )}

      {isTitular && !covered && !blocking && (
        <>
          <Button onClick={pay} disabled={busy} className="w-full h-11 bg-gradient-to-br from-emerald-500 to-blue-600 hover:opacity-90" data-testid="pay-quota-btn">
            <CreditCard className="h-4 w-4 mr-2" />
            Paga {euro(toPay)} con carta
          </Button>
          {late && (col.penalty.points > 0 || col.penalty.euro > 0) ? (
            <p className="text-xs text-red-600 flex items-start gap-1">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Pagamento in ritardo: {[
                col.penalty.points > 0 && `-${col.penalty.points} punti in classifica`,
                col.penalty.euro > 0 && `${euro(col.penalty.euro)} in più`,
              ].filter(Boolean).join(' e ')}.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Entro 30 minuti dall'inizio della partita, poi scatta la penalità della lega.</p>
          )}
        </>
      )}

      {!blocking && onPayOthers && (
        <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={onPayOthers} disabled={busy}>
          Pago anche per altri (carta)
        </Button>
      )}

      {data.is_admin && (
        <button type="button" onClick={cancel} disabled={busy} className="block text-xs text-destructive hover:underline">
          Annulla raccolta
        </button>
      )}
    </div>
  );
};

/** Shown to a player asked to collect (notification → ?collega=stripe): link and check their Stripe. */
export const CollectorStripeCard = ({ matchId }) => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    connectAPI.status().then((res) => setStatus(res.data)).catch(() => setStatus({}));
  }, []);

  const link = async () => {
    try {
      const res = await connectAPI.link({ origin: window.location.origin, return_path: `/matches/${matchId}?collega=stripe` });
      await openCheckout(res.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Non riesco ad aprire Stripe');
    }
  };

  if (!status) return null;
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="collector-stripe">
      <p className="text-sm font-medium">Raccogli tu i soldi del campo</p>
      {status.ready ? (
        <p className="text-sm text-emerald-700 dark:text-emerald-300">
          Il tuo conto Stripe è verificato: avvisa l'organizzatore che può sceglierti.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            Collega una volta il tuo conto Stripe (documento e IBAN, 5 minuti): le quote dei giocatori arriveranno lì.
          </p>
          <Button size="sm" onClick={link}>{status.connected ? 'Completa la verifica su Stripe' : 'Collega il tuo conto Stripe'}</Button>
        </>
      )}
    </div>
  );
};

export default FieldCollection;
